import type { LeaveRequest } from "@/features/leave/types";
import type { LeaveRequestDisplayItem, ScheduledListItem } from "../types";
import { isSameDayInZone } from "./scheduledShiftRange";

function toStartOfDayIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

function toEndOfDayIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

function overlapsRange(
  leaveRequest: LeaveRequest,
  rangeStartIso: string,
  rangeEndIso: string
): boolean {
  const start = Date.parse(toStartOfDayIso(leaveRequest.start_date));
  const end = Date.parse(toEndOfDayIso(leaveRequest.end_date));
  const rangeStart = Date.parse(rangeStartIso);
  const rangeEndExclusive = Date.parse(rangeEndIso);

  if (
    Number.isNaN(start) ||
    Number.isNaN(end) ||
    Number.isNaN(rangeStart) ||
    Number.isNaN(rangeEndExclusive)
  ) {
    return false;
  }

  return start < rangeEndExclusive && end >= rangeStart;
}

export function leaveRequestToDisplayItem(
  leaveRequest: LeaveRequest
): LeaveRequestDisplayItem {
  return {
    kind: "leave",
    id: leaveRequest.id,
    leave_request_id: leaveRequest.id,
    leave_type_id: leaveRequest.leave_type_id,
    start_date: leaveRequest.start_date,
    end_date: leaveRequest.end_date,
    booking_unit: leaveRequest.booking_unit,
    requested_days: leaveRequest.requested_days,
    reason: leaveRequest.reason,
    status: leaveRequest.status,
    rejection_reason: leaveRequest.rejection_reason,
    cancelled_at: leaveRequest.cancelled_at,
    created_at: leaveRequest.created_at,
    updated_at: leaveRequest.updated_at,
    planned_start_at: toStartOfDayIso(leaveRequest.start_date),
    planned_end_at: toEndOfDayIso(leaveRequest.end_date),
    timezone: "UTC",
  };
}

export function mergeAndSortTimelineItems(
  shiftItems: ScheduledListItem[],
  leaveRequests: LeaveRequest[],
  rangeStartIso: string,
  rangeEndIso: string,
  options: { openShiftId?: number | null; now?: Date } = {}
): ScheduledListItem[] {
  const leaveItems = leaveRequests
    .filter((request) => overlapsRange(request, rangeStartIso, rangeEndIso))
    .map(leaveRequestToDisplayItem);

  const { openShiftId = null, now = new Date() } = options;
  const merged = [...shiftItems, ...leaveItems];
  merged.sort((a, b) => {
    const aPriority = getTimelinePriority(a, openShiftId, now);
    const bPriority = getTimelinePriority(b, openShiftId, now);
    if (aPriority !== bPriority) return aPriority - bPriority;
    return Date.parse(a.planned_start_at) - Date.parse(b.planned_start_at);
  });
  return merged;
}

function getTimelinePriority(
  item: ScheduledListItem,
  openShiftId: number | null,
  now: Date
): number {
  if (item.kind !== "shift") return 2;

  const isInProgress =
    openShiftId != null &&
    item.actual_shift_id != null &&
    item.actual_shift_id === openShiftId;
  if (isInProgress) return 0;

  const startsAtMs = Date.parse(item.planned_start_at);
  const isUpcomingToday =
    !Number.isNaN(startsAtMs) &&
    startsAtMs >= now.getTime() &&
    isSameDayInZone(item.planned_start_at, item.timezone, now);

  return isUpcomingToday ? 1 : 2;
}
